import type {
  BlockExport,
  ComponentBlockExport,
  DroppableData,
  SerializableBlockExport,
  SerializableComponentBlockExport,
  TemplateBlockExport,
} from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type {
  BaseBlock,
  BaseProp,
  ChildrenProp,
  RootBlock,
} from "@circle-react-shared/BuilderUI/DataModel.types";
import type { PropsMeta } from "@circle-react-shared/BuilderUI/PropsMeta.types";
import type { PropsMetaStyles } from "@circle-react-shared/BuilderUI/PropsMetaStyle.types";
import type { NonEmptyArray } from "@circle-react-shared/BuilderUI/PropsMetaUnits.types";
import type { StylesProp } from "@circle-react-shared/BuilderUI/StyleValue.types";
import {
  AllCategoryNames,
  type BlockCategoryName,
} from "../constants/BlockCategories";
import { isSerializable } from "./isSerializable";

export const isProp = (prop: unknown): prop is BaseProp =>
  typeof prop === "object" &&
  prop !== null &&
  "type" in prop &&
  typeof prop.type === "string" &&
  "value" in prop;

export const isPropRecord = (prop: unknown): prop is Record<string, BaseProp> =>
  typeof prop === "object" &&
  prop !== null &&
  Object.values(prop).every(isProp);

export const isChildrenProp = (prop: unknown): prop is ChildrenProp =>
  isProp(prop) && prop.type === "children";

export const isBaseBlock = (value: unknown): value is BaseBlock =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  typeof value.type === "string" &&
  "id" in value &&
  typeof value.id === "string" &&
  "props" in value &&
  isPropRecord(value.props);

export const isRootBlock = (block: BaseBlock): block is RootBlock =>
  block.type === "root";

export const isBlockArray = (value: unknown): value is BaseBlock[] =>
  Array.isArray(value) && value.every(isBaseBlock);

export const isNonEmptyBlockArray = (
  value: unknown,
): value is NonEmptyArray<BaseBlock> => isBlockArray(value) && value.length > 0;

export const isNonNullableUnknown = (
  value: unknown,
): value is NonNullable<unknown> => value !== null && value !== undefined;

export const isTemplateExport = (
  value: unknown,
): value is TemplateBlockExport =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  value.type === "template" &&
  "templateId" in value &&
  typeof value.templateId === "string" &&
  "templateData" in value &&
  isNonEmptyBlockArray(value.templateData) &&
  "meta" in value &&
  typeof value.meta === "object" &&
  value.meta !== null &&
  "name" in value.meta &&
  typeof value.meta.name === "string" &&
  isSerializable(value);

export const isComponentExport = (
  value: unknown,
): value is ComponentBlockExport =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  value.type === "component" &&
  "defaultData" in value &&
  isBaseBlock(value.defaultData) &&
  "meta" in value &&
  typeof value.meta === "object" &&
  value.meta !== null &&
  "Block" in value &&
  value.Block !== null;

export const isSerializableComponentExport = (
  value: unknown,
): value is SerializableComponentBlockExport =>
  typeof value === "object" &&
  value !== null &&
  "type" in value &&
  value.type === "component" &&
  "defaultData" in value &&
  isBaseBlock(value.defaultData) &&
  "meta" in value &&
  typeof value.meta === "object" &&
  value.meta !== null &&
  isSerializable(value);

export const isStylesProp = (prop: unknown): prop is StylesProp =>
  isProp(prop) && prop.type === "styles";

export const isBlockExport = (value: unknown): value is BlockExport =>
  isComponentExport(value) || isTemplateExport(value);

export const isSerializableBlockExport = (
  value: unknown,
): value is SerializableBlockExport =>
  isSerializableComponentExport(value) || isTemplateExport(value);

export const isDroppableData = (value: unknown): value is DroppableData =>
  typeof value === "object" &&
  value !== null &&
  "accepts" in value &&
  Array.isArray(value.accepts) &&
  value.accepts.every(accept => typeof accept === "string") &&
  "rejects" in value &&
  Array.isArray(value.rejects) &&
  value.rejects.every(reject => typeof reject === "string") &&
  "path" in value &&
  typeof value.path === "string";

export const isPropsMeta = (value: unknown): value is PropsMeta =>
  typeof value === "object" &&
  value !== null &&
  "prop" in value &&
  typeof value.prop === "string";

export const isPropsMetaStyles = (value: unknown): value is PropsMetaStyles =>
  isPropsMeta(value) && value.prop === "styles";

export const isBlockCategoryName = (
  value: unknown,
): value is BlockCategoryName =>
  typeof value === "string" &&
  AllCategoryNames.includes(value as BlockCategoryName);

export const isBlockCategory = (value: unknown): value is BlockCategoryName =>
  typeof value === "object" &&
  value !== null &&
  "name" in value &&
  typeof value.name === "string" &&
  isBlockCategoryName(value.name);

export const isBlockCategoryNameArray = (
  value: unknown,
): value is BlockCategoryName[] =>
  Array.isArray(value) && value.every(isBlockCategoryName);
